"use client";
import type { CSSProperties, ReactElement } from "react";
import { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { TOOLTIP_BACKGROUND, TOOLTIP_BORDER } from "./gantt-constants";
import { getGanttTooltipPosition } from "./gantt-tooltip-position";
import { GanttTooltipDateRange } from "./GanttTooltipDateRange";
import { GanttTooltipDuration } from "./GanttTooltipDuration";
import { GanttTooltipStatusBadge } from "./GanttTooltipStatusBadge";
import { GanttTooltipTitle } from "./GanttTooltipTitle";
import type { GanttTooltipProps } from "./GanttTooltip.types";
import { useGanttTooltipDimensions } from "./useGanttTooltipDimensions";
export type { GanttTooltipContent, GanttTooltipProps } from "./GanttTooltip.types";
const GANTT_TOOLTIP_SHELL_STYLE: CSSProperties = {
    position: "absolute",
    pointerEvents: "none",
    backgroundColor: TOOLTIP_BACKGROUND,
    border: TOOLTIP_BORDER,
    borderRadius: 10,
    padding: "8px 10px",
    minWidth: 180,
    zIndex: 40,
    boxShadow: "0 12px 24px -14px rgba(0,0,0,0.7)",
};
export function GanttTooltip({ tooltip }: GanttTooltipProps): ReactElement {
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const tooltipContentTaskLabel = tooltip?.content.taskLabel;
    const tooltipDimensions = useGanttTooltipDimensions({
        tooltipRef,
        tooltipContentTaskLabel,
    });
    const clampedPosition = tooltip
        ? getGanttTooltipPosition({ tooltip, tooltipDimensions })
        : null;
    const tooltipIdentity = tooltip?.content.taskLabel;
    return (<AnimatePresence>
      {tooltip && clampedPosition ? (
          <motion.div data-el="Cmarkdowngantt/Tooltip/Root" ref={tooltipRef} key={`gantt-tooltip-${tooltip.content.taskLabel}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.16, ease: "easeOut" }} style={{
                ...GANTT_TOOLTIP_SHELL_STYLE,
                left: clampedPosition.left,
                top: clampedPosition.top,
            }}>
            <GanttTooltipTitle taskLabel={tooltip.content.taskLabel}/>
            <GanttTooltipDateRange startLabel={tooltip.content.startLabel} endLabel={tooltip.content.endLabel}/>
            <GanttTooltipDuration durationDays={tooltip.content.durationDays}/>
            <GanttTooltipStatusBadge status={tooltip.content.status} statusLabel={tooltip.content.statusLabel} statusColor={tooltip.content.statusColor}/>
          </motion.div>
        ) : null}
    </AnimatePresence>);
}
